/**
 * AvatarUrlResolver — selects the best available avatar representation.
 *
 * The avatar-system spec defines three states an avatar can be in, each
 * with a valid rendering. This module centralizes the decision so every
 * rendering surface (Phaser scenes, WelcomeBack cards, Neighborhood
 * entrances, Top bar avatar badge, etc.) uses the same logic.
 *
 * Priority (highest first):
 *   1. Walking sprite atlas (fully generated)  → render with walk cycle
 *   2. Static portrait (FLUX done, autosprite pending/failed) → bounce
 *   3. Silhouette placeholder → bounce
 *
 * Per world-navigation-experience Reqs 5.1, 13.2, 21.2 (all three render
 * states are valid and must be handled gracefully across surfaces).
 *
 * Per avatar-system spec Req 6 (Silent_Fallback) — callers SHALL NEVER
 * surface an error when the walking sprite is missing; the bouncing
 * static portrait is always an acceptable fallback.
 */

import { API_BASE } from "../api";

export type AvatarRenderKind = "walking" | "bouncing_portrait" | "silhouette";

/** Normalize avatar URLs: ensure /avatars/ paths go through the /avatars/images/ proxy with full API base. */
export function proxyAvatarUrl(url: string): string {
  if (url.startsWith("http")) return url;
  if (url.startsWith("/avatars/images/")) return `${API_BASE}${url}`;
  if (url.startsWith("/avatars/")) return `${API_BASE}${url.replace(/^\/avatars\//, "/avatars/images/")}`;
  return url;
}

export interface AvatarRow {
  // From avatar-system spec Req 13 schema. All fields optional because
  // the row may exist at various stages of completeness.
  walk_down_url?: string | null;
  walk_right_url?: string | null;
  walk_up_url?: string | null;
  walk_down_atlas_url?: string | null;
  walk_right_atlas_url?: string | null;
  walk_up_atlas_url?: string | null;
  walk_status?: string | null;
  static_full_url?: string | null;
  portrait_500_url?: string | null;
  portrait_256_url?: string | null;
  portrait_128_url?: string | null;
  portrait_64_url?: string | null;
}

export interface AvatarRenderPlan {
  kind: AvatarRenderKind;
  /** Primary rendering URL. For 'walking' this is walk_down (south-facing). */
  primaryUrl: string;
  /**
   * Walking atlas URLs for all three generated directions, if kind === 'walking'.
   * West is derived by flipping walk_right; no URL stored for it.
   */
  walkAtlas?: {
    down: string;
    right: string;
    up: string;
    downAtlas?: string;
    rightAtlas?: string;
    upAtlas?: string;
  };
  /** True when the UI should animate a gentle bounce. */
  shouldBounce: boolean;
}

/**
 * Single androgynous silhouette used for all users without a custom avatar.
 * No longer varies by userId — eliminates gender mismatch frustration.
 */

/** @deprecated No longer varies by userId. Kept for API compat. */
export function silhouetteGender(_userId?: string | null): "male" | "female" {
  return "female"; // unused — kept only so callers don't break at compile time
}

/** Get silhouette placeholder URLs (same for all users). */
export function getSilhouetteUrls(_userId?: string | null) {
  return {
    full: `/sprites/silhouettes/androgenous_avatar.png`,
    portrait_500: `/sprites/silhouettes/androgenous_portrait_500.png`,
    portrait_256: `/sprites/silhouettes/androgenous_portrait_256.png`,
    portrait_128: `/sprites/silhouettes/androgenous_portrait_128.png`,
    portrait_64: `/sprites/silhouettes/androgenous_portrait_64.png`,
  };
}

/** Get silhouette walk sprite URLs (same for all users). */
export function getSilhouetteWalkUrls(_userId?: string | null) {
  return {
    down: `/sprites/silhouettes/androgenous_iso_walk_down.png`,
    right: `/sprites/silhouettes/androgenous_iso_walk_right.png`,
    up: `/sprites/silhouettes/androgenous_iso_walk_up.png`,
  };
}

/** Path to the committed placeholder silhouette asset. */
export const SILHOUETTE_URL = `/sprites/silhouettes/androgenous_avatar.png`;

/** Placeholder URLs (same for all users). */
export const PLACEHOLDER_URLS = {
  full: `/sprites/silhouettes/androgenous_avatar.png`,
  portrait_500: `/sprites/silhouettes/androgenous_portrait_500.png`,
  portrait_256: `/sprites/silhouettes/androgenous_portrait_256.png`,
  portrait_128: `/sprites/silhouettes/androgenous_portrait_128.png`,
  portrait_64: `/sprites/silhouettes/androgenous_portrait_64.png`,
} as const;

/** Silhouette walk sprite URLs. */
export const SILHOUETTE_WALK_URLS = {
  down: `/sprites/silhouettes/androgenous_iso_walk_down.png`,
  right: `/sprites/silhouettes/androgenous_iso_walk_right.png`,
  up: `/sprites/silhouettes/androgenous_iso_walk_up.png`,
} as const;

/**
 * Given an avatar row (possibly null for brand-new users), return the
 * best-available rendering plan. Always returns a valid plan — never
 * throws, never returns null. The silhouette is the baseline.
 */
export function resolveAvatar(row: AvatarRow | null | undefined): AvatarRenderPlan {
  if (!row) {
    return {
      kind: "silhouette",
      primaryUrl: SILHOUETTE_URL,
      shouldBounce: true,
    };
  }

  // Walking sprite is available iff walk_status is succeeded AND all three
  // directional URLs are present. Per avatar spec Req 6.3, edge cases where
  // partial succeeds happen are handled by falling back to the static portrait.
  const hasAllDirections =
    !!row.walk_down_url && !!row.walk_right_url && !!row.walk_up_url;
  const walkReady = row.walk_status === "succeeded" && hasAllDirections;

  if (walkReady) {
    return {
      kind: "walking",
      primaryUrl: proxyAvatarUrl(row.walk_down_url!),
      walkAtlas: {
        down: proxyAvatarUrl(row.walk_down_url!),
        right: proxyAvatarUrl(row.walk_right_url!),
        up: proxyAvatarUrl(row.walk_up_url!),
        downAtlas: row.walk_down_atlas_url ? proxyAvatarUrl(row.walk_down_atlas_url) : undefined,
        rightAtlas: row.walk_right_atlas_url ? proxyAvatarUrl(row.walk_right_atlas_url) : undefined,
        upAtlas: row.walk_up_atlas_url ? proxyAvatarUrl(row.walk_up_atlas_url) : undefined,
      },
      shouldBounce: false,
    };
  }

  // Static portrait is available iff we have either the full or any crop.
  const staticUrl =
    row.portrait_256_url ??
    row.portrait_500_url ??
    row.portrait_128_url ??
    row.portrait_64_url ??
    row.static_full_url;

  if (staticUrl) {
    return {
      kind: "bouncing_portrait",
      primaryUrl: proxyAvatarUrl(staticUrl),
      shouldBounce: true,
    };
  }

  return {
    kind: "silhouette",
    primaryUrl: SILHOUETTE_URL,
    shouldBounce: true,
  };
}

/**
 * Shortcut for UIs that just need a URL (compact profile card, etc.) —
 * gives back a reasonable URL regardless of state.
 */
export function avatarUrl(row: AvatarRow | null | undefined): string {
  return resolveAvatar(row).primaryUrl;
}

/**
 * Pick the right directional walk sheet for an in-world avatar facing
 * a given direction. Returns a pair (url, shouldFlipHorizontal) so the
 * caller can apply the flip for west-facing without needing to know
 * which direction is derived.
 */
export type WalkDirection = "north" | "south" | "east" | "west";

export function pickWalkDirection(
  plan: AvatarRenderPlan,
  direction: WalkDirection,
): { url: string; flipH: boolean } {
  if (plan.kind !== "walking" || !plan.walkAtlas) {
    // For non-walking plans, return the primary URL with no flip —
    // callers should use the bouncing animation instead of a walk cycle.
    return { url: plan.primaryUrl, flipH: false };
  }
  switch (direction) {
    case "south":
      return { url: plan.walkAtlas.down, flipH: false };
    case "north":
      return { url: plan.walkAtlas.up, flipH: false };
    case "east":
      return { url: plan.walkAtlas.right, flipH: false };
    case "west":
      // West is derived by flipping east
      return { url: plan.walkAtlas.right, flipH: true };
  }
}
